import * as _ from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import type { ChangeEvent, VoidFunctionComponent } from 'react';

import COLORS from '../../../theme/colors';
import { roundToNthDecimalPlace } from '../../../utils';

import styles from './RangeSlider.module.less';

export type RangeTuple = [number, number];

/**
 * Small hook to memoize the background style for the range slider.
 */
const useRangeSliderBackground = (min: number, max: number, [minValue, maxValue]: RangeTuple) => useMemo(() => {
  const minPercent = ((minValue - min) / (max - min)) * 100;
  const maxPercent = ((maxValue - min) / (max - min)) * 100;

  return {
    background: `linear-gradient(to right, ${COLORS.fossaGray10} ${minPercent}%, ${COLORS.fossaGreen} ${minPercent}%, ${COLORS.fossaGreen} ${maxPercent}%, ${COLORS.fossaGray10} ${maxPercent}%)`,
  };
}, [max, maxValue, min, minValue]);

//  Determine the left position of the label based on the value
const calculateLabelPercentage = (value: number, min: number, max: number): number => {
  return ((value - min) / (max - min)) * 100;
};

//  The max offset on either side is 8px, so the "range" is -8 to 8,
//  where 100% = -8px and 0% = +8px
const calculateLabelOffset = (percent: number) => {
  return -((percent / 100) * 16 - 8);
};

/**
 * Small hook to memoize the label styles for the range slider.
 * It uses a layout effect to measure the width of the container and the labels.
 */
const useRangeSliderLabelsStyle = (min: number, max: number, [minValue, maxValue]: RangeTuple) => {
  const minPercent = calculateLabelPercentage(minValue, min, max);
  const minOffset = calculateLabelOffset(minPercent);

  const maxPercent = calculateLabelPercentage(maxValue, min, max);
  const maxOffset = calculateLabelOffset(maxPercent);

  return {
    minValue: useMemo(() => ({ left: `calc(${minPercent}% + ${minOffset}px)` }), [minPercent, minOffset]),
    maxValue: useMemo(() => ({ left: `calc(${maxPercent}% + ${maxOffset}px)` }), [maxPercent, maxOffset]),
  };
};

//  Ensures that a number is finite before returning it, otherwise returns null.
//  Also rounds the number to 2 decimal places, to avoid weird floating point issues.
const validateNumber = (value: number): number | null => {
  if (!Number.isFinite(value)) {
    return null;
  }
  return roundToNthDecimalPlace(value, 2);
};

type Props = {
  id: string;
  value: RangeTuple;
  min: number;
  max: number;
  step?: number;
  onChange: (value: RangeTuple) => void;
  debounceTimeout?: number;
};

/**
 * RangeSlider is a component which allows users to select a pair of "range" values within a min/max range.
 *
 * @param {Props} props - The props of the component.
 * @param {string} props.id - The id of the component.
 * @param {RangeTuple} props.value - The current value of the range slider.
 * @param {number} props.min - The minimum value of the range slider.
 * @param {number} props.max - The maximum value of the range slider.
 * @param {number} [props.step] - The step of the range slider.
 * @param {(value: RangeTuple) => void} props.onChange - The callback to be called when the value changes.
 * @param {number} [props.debounceTimeout] - How long the component should wait before calling the onChange callback (default: 100ms).
 */
const RangeSlider: VoidFunctionComponent<Props> = ({
  id,
  value,
  min,
  max,
  step = 1,
  onChange,
  debounceTimeout = 100,
}) => {
  //  The value the user has dragged to that `onChange` has not reported yet. It wins over `value` until then.
  const [pendingValue, setPendingValue] = useState<RangeTuple | null>(null);
  const currentValue = pendingValue ?? value;
  const [minValue, maxValue] = currentValue;
  //  We want *some* level of debouncing here, because the actual input can be dragged, which triggers a lot of events.
  const debouncedOnChange = useMemo(() => _.debounce((newValue: RangeTuple) => {
    onChange(newValue);
    //  Only clear the value we just reported, so a newer drag is not dropped.
    setPendingValue((pending) => (pending === newValue ? null : pending));
  }, debounceTimeout), [onChange, debounceTimeout]);
  //  Update the value immediately, and then debounce the onChange callback.
  const onChangeWithDebounce = useCallback((newValue: RangeTuple) => {
    setPendingValue(newValue);
    debouncedOnChange(newValue);
  }, [debouncedOnChange]);

  //  useCallback here because the range slider can be dragged, which triggers a lot of events.
  const onMinChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newMin = validateNumber(Math.min(Number(event.target.value), maxValue - step));
    //  noop if the number is not finite.
    if (newMin === null) {
      return;
    }
    onChangeWithDebounce([newMin, maxValue]);
  }, [maxValue, onChangeWithDebounce, step]);

  //  useCallback here because the range slider can be dragged, which triggers a lot of events.
  const onMaxChange = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const newMax = validateNumber(Math.max(Number(event.target.value), minValue + step));
    //  noop if the number is not finite.
    if (newMax === null) {
      return;
    }
    onChangeWithDebounce([minValue, newMax]);
  }, [minValue, onChangeWithDebounce, step]);

  const labelStyles = useRangeSliderLabelsStyle(min, max, currentValue);

  return (
    <div id={id} className={styles.rangeSlider}>
      <div className={styles.rangeSliderTrackContainer}>
        <div className={styles.rangeSliderTrack} style={useRangeSliderBackground(min, max, currentValue)} role="presentation" />
        <input
          type="range"
          className={styles.rangeSliderThumb}
          min={min}
          max={max}
          value={minValue}
          onChange={onMinChange}
          aria-label="Minimum value"
          step={step}
        />
        <input
          type="range"
          className={styles.rangeSliderThumb}
          min={min}
          max={max}
          value={maxValue}
          onChange={onMaxChange}
          aria-label="Maximum value"
          step={step}
        />
      </div>
      <div className={styles.rangeSliderLabelsContainer}>
        <span className={styles.rangeSliderLabel} style={labelStyles.minValue}>
          {/* We round again down here because there are edge cases because JS is weird */}
          {roundToNthDecimalPlace(minValue).toString()}
        </span>
        <span className={styles.rangeSliderLabel} style={labelStyles.maxValue}>
          {roundToNthDecimalPlace(maxValue).toString()}
        </span>
      </div>
    </div>
  );
};

export default RangeSlider;
